/**
 * The menu as schema.org, built from the same `data/menu.json` the page
 * renders — 8 categories and 41 items whose prices were, until this existed,
 * readable only as `€X.XX` text in whichever of five languages the crawler
 * happened to land on.
 *
 * `buildMenuSchema()` is a plain function taking the categories and a
 * translator, rather than a composable that reaches for `useI18n()` itself, so
 * `tests/structuredData.test.ts` can call it outside the Nuxt environment —
 * the same reason `composables/useSiteSearch.ts` exports `rankSearchResults()`
 * as a pure function.
 */

/** The shape `/api/menu` returns; mirrors `data/menu.json`. */
export interface MenuSchemaItem {
    key: string;
    price: number;
    note?: string;
}

export interface MenuSchemaCategory {
    id: string;
    icon: string;
    labelKey: string;
    items: MenuSchemaItem[];
}

type Translate = (key: string) => string;

export interface MenuNode {
    '@type': 'Menu';
    name: string;
    inLanguage: string;
    hasMenuSection: {
        '@type': 'MenuSection';
        name: string;
        hasMenuItem: {
            '@type': 'MenuItem';
            name: string;
            offers: {
                '@type': 'Offer';
                price: number;
                priceCurrency: string;
            };
        }[];
    }[];
}

/**
 * @param categories what `useFetch('/api/menu')` returned — null while it is in
 *   flight or if it failed, in which case there is no menu to describe and the
 *   caller should emit nothing.
 * @param t resolves an i18n key in the locale being read.
 * @param currency ISO 4217, from `data/refuge.ts`.
 */
export function buildMenuSchema(
    categories: MenuSchemaCategory[] | null | undefined,
    t: Translate,
    currency: string,
    locale: string,
    menuName: string,
): MenuNode | null {
    if (!categories?.length) return null;

    return {
        '@type': 'Menu',
        name: menuName,
        inLanguage: locale,
        hasMenuSection: categories.map(category => ({
            '@type': 'MenuSection',
            name: t(category.labelKey),
            hasMenuItem: category.items.map(item => ({
                '@type': 'MenuItem',
                name: t(item.key),
                offers: {
                    '@type': 'Offer',
                    // Numeric, deliberately: the page formats this as `€X.XX`
                    // for a reader, but schema.org wants the bare figure.
                    price: item.price,
                    priceCurrency: currency,
                },
            })),
        })),
    };
}
